import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  TextField,
  MenuItem,
  Box,
  Chip,
  Typography,
  IconButton,
} from "@mui/material";
import pick from "lodash/pick";
import keys from "lodash/keys";
import get from "lodash/get";
import { CloseOutlined } from "@mui/icons-material";
import DatePickerField from "components/DateComponets/DatePicker";
import isUndefined from "lodash/isUndefined";

import {
  AddWarrantyForm,
  getBase64,
  createWarrantyFieldExtraProps,
} from "./warrantyUtils";
import { addUpdateWarranty } from "actions/warrantyActions";
import { PROJECT_ADD_WARRANTY_SUCCESS } from "constants/types";
import AppRoleContext from "context/AppRoleContext";
import {
  isAssignee,
  isHomeOwner,
  isProjectManager,
  isSupplierAdmin,
} from "core/utils/roleUtils";
import { getJobsList } from "selectors/jobSelectors";
import { jobGetAll } from "actions/jobActions";

export const AddNewWarranty = ({
  onCancel = () => {},
  projectData = {},
  propertyData = {},
  selectedJob,
  title,
  propData,
  params,
  onSave = () => {},
}) => {
  const appRoleDetails = useContext(AppRoleContext);

  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [file, setFile] = useState(null);

  const { jobsList } = useSelector(getJobsList);

  const dynamicOptions = {
    jobId: jobsList.map((job) => ({
      key: job.jobId,
      value: job.jobName,
      text: job.jobName,
      data: job,
    })),
  };

  const isSupplier =
    isSupplierAdmin(appRoleDetails) || isAssignee(projectData?.accessType);
  const isBuilder =
    isProjectManager(appRoleDetails, projectData?.projUserRole) &&
    !isAssignee(projectData?.accessType);
  const isHomeowner = isHomeOwner(appRoleDetails);

  useEffect(() => {
    formik.setFieldValue("logo1", file);
  }, [file]);

    const fetchRecieptentOptions = useCallback(() => {
      const queryData = {
        pageNumber: 1,
        pageSize: 500,
        projectId: projectData.projectId,
      };
      dispatch(jobGetAll(queryData));
      if (isSupplier) {
        if (selectedJob && selectedJob.data?.warrantyId) {
          return;
        } else {
          formik.setFieldValue(
            "clientType",
            isSupplier
              ? "contractor"
              : ""
          );
        }
      }
  
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isBuilder, isSupplier]);

    useEffect(() => {
    fetchRecieptentOptions();
  }, [fetchRecieptentOptions]);

  const { steps, formFields, validationSchema, stepDescription } =
    AddWarrantyForm;
  const initialState = AddWarrantyForm.defaultState(
    get(selectedJob, "data", null)
  );

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: validationSchema,
    onSubmit: () => {
      console.log("Check the Onsubmit : ");
    },
  });
  const handleNext = () => {
    const currentFields = formFields[`step${activeStep}`]
      .filter((field) => field.required)
      .map((field) => field.key);
    const currentTouchedFields = pick(formik.touched, currentFields);
    const currentErrorFields = pick(formik.errors, currentFields);
    if (activeStep + 1 < steps.length) {
      if (
        currentFields.length === keys(currentTouchedFields).length &&
        keys(currentErrorFields).length === 0
      ) {
        setActiveStep(activeStep + 1);
      }
    }
    if (activeStep + 1 === steps.length) {
      if (keys(formik.errors).length === 0) {
        dispatch(
          addUpdateWarranty({
            //...formik.values
            formData: {
              ...formik.values,
              PropertyId: params.propertyId,
              ProjectId: params.projectId,
            },
            onSuccess: (payload, warrantyId) => {
              console.log("Files Response", payload);
              if (!payload) {
                onCancel(false);
                if (onSave) {
                  onSave(payload);
                }
                return;
              }
              let receipt = "";
              let productImage = "";
              let productPhotos1 = "";
              let receipts1 = "";
              payload?.forEach((f) => {
                if (f.status === "fulfilled") {
                  if (f.value.data.documentLabel.includes("rcpt_")) {
                    receipt = f.value.data.documentId;
                    receipts1 = f.value.data.fileName;
                  } else if (
                    f.value.data.documentLabel.includes("prodImages_")
                  ) {
                    productImage = f.value.data.documentId;
                    productPhotos1 = f.value.data.fileName;
                  }
                } else {
                  console.warn("File upload failed: ", f.reason);
                }
              });

              dispatch(
                addUpdateWarranty({
                  formData: {
                    ...formik.values,
                    warrantyId: warrantyId,
                    receiptId: receipt,
                    productPhotoId: productImage,
                    PropertyId: params.propertyId,
                    ProjectId: params.projectId,
                    receipts1,
                    productPhotos1,
                  },
                  onSuccess: () => {
                    onCancel(false);
                    if (onSave) {
                      onSave(payload);
                    }
                  },
                  updateFiles: false,
                })
              );
            },
            payload: propData,
            updateFiles: true,
            CloseAddView: () => {
              onCancel(false);
            },
          })
        );
      }
    }
  };

  return (
    <div className="mt-2 p-2">
      <div className="d-flex justify-content-between">
        <Typography className="text-left m-2 h1" id="header">
          {title || "Add a Warranty"}
        </Typography>
        <IconButton onClick={() => onCancel(false)}>
          <CloseOutlined />
        </IconButton>
      </div>

      <div className="">
        <div>
          <Typography variant="subtitle1" className="text-left m-1">
            {stepDescription[`step${activeStep}`].heading
              ? `${activeStep + 1}. ${
                  stepDescription[`step${activeStep}`].heading
                }`
              : ""}
          </Typography>
          <div>
            <p className="m-1">
              <sup style={{ color: "red", fontSize: "1em", top: 0 }}>*</sup>
              <b>{stepDescription[`step${activeStep}`].subHeading}</b>
            </p>
          </div>
        </div>
        <div>
          <form onSubmit={formik.handleSubmit}>
            <div className="stepContent container row">
              {formFields[`step${activeStep}`].map((formField, index) => {
                let field = formField
                if (field.dynamicField) {
                field = {
                  ...field,
                  options: dynamicOptions[field.key]
                }
                }
                let extraProps = get(field, "extraInputProps", {});
                if (createWarrantyFieldExtraProps[field.key]) {
                  extraProps = {
                    ...extraProps,
                    ...createWarrantyFieldExtraProps[field.key][
                      isSupplier ? "supplier" : "builder"
                    ](formik),
                  };
                }
                if (isHomeOwner(appRoleDetails) && field.key === "jobId") {
                  return null
                }
                return (
                  <div
                    className={`${field.className} mt-3`}
                    style={{ width: field.width }}
                  >
                    <p style={{ marginBottom: "0.5rem" }}>
                      <label className="form-label">
                        {field.label}
                        {(field.required || extraProps.required) && (
                          <sup
                            style={{ color: "red", fontSize: "1em", top: 0 }}
                          >
                            *
                          </sup>
                        )}
                      </label>
                    </p>
                    {field.type === "dateField" ? (
                      <DatePickerField
                        onChange={(date) => {
                          formik.setFieldValue(field.key, date);
                        }}
                        value={formik.values[field.key]}
                      />
                    ) : (
                      <TextField
                        key={field.key}
                        id={field.key}
                        type={field.type}
                        select={field.select}
                        name={field.key}
                        fullWidth
                        variant="outlined"
                        placeholder={field.placeholder}
                        className={`registration-formField `}
                        value={formik.values[field.key]}
                        disabled={field.disabled}
                        onChange={(e) => {
                          if (
                            field.key === "receipts" ||
                            field.key === "productPhotos"
                          ) {
                            let d = {};
                            formik.setFieldValue(
                              `${field.key}1`,
                              e.target.files[0]
                            );
                            d[field.key] = e.target.value;
                            setFile(d);
                          } else {
                            if (field.formatter) {
                              const formattedValue = field.formatter(
                                e.target.value
                              );
                              formik.setFieldValue(field.key, formattedValue);
                            } else {
                              formik.handleChange(e);
                            }
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched[field.key] &&
                          Boolean(formik.errors[field.key])
                        }
                        helperText={
                          formik.touched[field.key] && formik.errors[field.key]
                        }
                        SelectProps={{
                          displayEmpty: true,
                          multiple: field.multiple,
                          renderValue: (selected, x, y, z) => {
                            if (field.multiple) {
                              if (selected && selected.length === 0) {
                                return <em>{field.placeholder}</em>;
                              }
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    marginTop: "-0.75rem",
                                  }}
                                >
                                  {selected &&
                                    selected.length > 0 &&
                                    selected.map((value) => (
                                      <Chip
                                        key={value}
                                        label={value}
                                        style={{ fontSize: "12px" }}
                                        variant="outlined"
                                        onDelete={() => {
                                          formik.setFieldValue(
                                            field.key,
                                            selected.filter(
                                              (item) => item !== value
                                            )
                                          );
                                        }}
                                      />
                                    ))}
                                </Box>
                              );
                            } else {
                              return (
                                <Typography>
                                  {typeof field.options[0] === "string"
                                    ? selected
                                    : field.options.find(
                                        (option) => option.key === selected
                                      )?.text}
                                </Typography>
                              );
                            }
                          },
                        }}
                      >
                        <MenuItem value={""} disabled>
                          {field.placeholder}
                        </MenuItem>
                        {field.options &&
                          field.options.map((option) => {
                            return !isUndefined(option.key) ? (
                              <MenuItem
                                key={option.key}
                                value={option.key}
                                selected={
                                  formik.values[field.key] === option.key
                                }
                              >
                                {option.text}
                              </MenuItem>
                            ) : (
                              <MenuItem value={option}>{option}</MenuItem>
                            );
                          })}
                      </TextField>
                    )}
                  </div>
                );
              })}
              <div style={{ textAlign: "right" }}>
                <div className="d-flex justify-content-between">
                  <div>
                    {activeStep !== 0 && (
                      <button
                        type="submit"
                        className={`save-details-button m-4 primaryButton`}
                        onClick={() => {
                          setActiveStep(activeStep - 1);
                        }}
                      >
                        {"< Previous"}
                      </button>
                    )}
                  </div>
                  <div className="float-right">
                    <button
                      type="submit"
                      className={`save-details-button m-4 primaryButton`}
                      onClick={() => {
                        handleNext();
                      }}
                    >
                      {selectedJob ? "Update" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddNewWarranty;
